import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
  type FlatJoinSplitV2TransactionRequest,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import { getBaseReadProvider } from "../utils/base-read-provider";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { assertNotAborted, decimalScalar, fail } from "./common";
import type { OperationLog } from "./operationTypes";
import { loadSpendTree, pinnedReadProvider } from "./poolAccess";
import { progressLog, sendSimulated } from "./settlement";
import type { FlatJoinSplitV2SwapOutputSeed } from "./swapOutput";
import { inputWitness, outputWitness, validateInputs } from "./witness";
import {
  prepareAtomicEvmRecovery,
  evmRecoveryOutputDeliveries,
} from "./onchainRecoveryEvmPublish";
import { formatInboxPublicKeyHex } from "../utils/inbox-crypto";
import { withReadCancellation } from "./readCancellation";
import { persistFlatJournalBackup } from "./journalBackup";
import {
  chooseFlatRelayRoute,
  assertFlatRelayFeeRecipient,
} from "./relayRouting";

export interface FlatJoinSplitV2SpendParams {
  signer: ethers.Signer;
  provider: ethers.Provider;
  pool: string;
  chainId: bigint;
  tokenAddress: string;
  selected: StoredJoinSplitNote[];
  outputs: JoinSplitNote[];
  spendingKeys: SpendingKeys;
  kind:
    | "joinsplit"
    | "withdraw"
    | "withdraw_batch"
    | "migrate"
    | "swap"
    | "bridge";
  /** For adapter actions this is the pool-owned adapter, not a user address. */
  recipient?: string;
  /** Batch withdrawals only: the ordered payout list bound into the proof. */
  recipients?: string[];
  amounts?: bigint[];
  withdrawAmount?: bigint;
  fee?: bigint;
  /** Adapter actions only: binds every adapter-visible parameter to the proof. */
  auxDataHash?: bigint;
  swapData?: FlatJoinSplitV2SwapData;
  swapOutputSeed?: FlatJoinSplitV2SwapOutputSeed;
  bridgeData?: FlatJoinSplitV2BridgeData;
  expectedOutputTreeIndex?: number;
  expectedOutputTokenHash?: string;
  deliveries?: FlatJoinSplitV2JournalDelivery[];
  onLog: OperationLog;
  /** Submit through the relayer instead of the user's own wallet. */
  relay?: boolean;
  expectedRelayerAddress?: string;
  /** Background maintenance must refuse wallet fallback without a prompt. */
  confirmFallback?: () => boolean | Promise<boolean>;
  /** Recheck an expiring sponsorship quote before any proof is disclosed. */
  beforeDisclosure?: () => Promise<void>;
  signal?: AbortSignal;
}

/** Build and locally verify a spend without journaling or broadcasting it. */
/**
 * D10: a chain's block gas limit caps the circuit arity a settlement may
 * use (DogeOS Chikyu: 10M gas, arity 2 = 8x5 at most). Base allows the
 * whole ladder. Checked before any proof is generated.
 */
export function assertFlatArityAllowed<T extends number>(
  arityId: T,
  maxArityId: number = EVM_CONFIG.JOINSPLIT_V2?.MAX_ARITY_ID ?? 4,
): T {
  if (arityId > maxArityId) {
    throw new Error(
      `This operation needs circuit arity ${arityId}, above the ${EVM_CONFIG.NETWORK?.label ?? "network"} limit of ${maxArityId}; split it into smaller operations`,
    );
  }
  return arityId;
}

export async function prepareFlatJoinSplitV2Spend(
  params: Omit<FlatJoinSplitV2SpendParams, "signer">,
): Promise<FlatJoinSplitV2TransactionRequest> {
  assertNotAborted(params.signal, "before witness construction");
  const sourceTree = params.selected[0].note.treeIndex!;
  if (params.selected.some((note) => note.note.treeIndex !== sourceTree)) {
    fail("one proof cannot consume notes from multiple trees");
  }
  const witnessStarted = Date.now();
  params.onLog("Reading deposit history for the proof", "info");
  const tree = await withReadCancellation(
    () => loadSpendTree(params.chainId, params.pool, sourceTree),
    params.signal,
  );
  params.onLog(
    `Deposit history ready (${((Date.now() - witnessStarted) / 1000).toFixed(1)}s)`,
    "info",
  );
  params.onLog("Validating private inputs", "info");
  const snapshot = await withReadCancellation(
    () =>
      readFlatJoinSplitV2PoolSnapshot(
        pinnedReadProvider(params.provider),
        params.pool,
        params.tokenAddress,
        tree.root,
      ),
    params.signal,
  );
  if (params.expectedRelayerAddress) {
    assertFlatRelayFeeRecipient(
      { relay: true, relayerAddress: params.expectedRelayerAddress },
      snapshot.feeRecipient,
      params.fee ?? 0n,
    );
  }
  if (
    params.expectedOutputTreeIndex !== undefined &&
    snapshot.currentTreeIndex !== BigInt(params.expectedOutputTreeIndex)
  ) {
    fail("pool rolled over while preparing the migration output");
  }
  if (
    params.expectedOutputTokenHash !== undefined &&
    snapshot.currentTokenHash.toLowerCase() !==
      params.expectedOutputTokenHash.toLowerCase()
  ) {
    fail("pool token domain changed while preparing the migration output");
  }
  const recovery = await prepareAtomicEvmRecovery({
    provider: params.provider,
    chainId: Number(params.chainId),
    pool: params.pool,
    outputs: evmRecoveryOutputDeliveries(
      params.outputs,
      params.spendingKeys,
      params.deliveries,
    ),
    seeds: params.swapOutputSeed
      ? [
          {
            seed: params.swapOutputSeed,
            pool: params.pool,
            inboxPublicKey: formatInboxPublicKeyHex(
              params.spendingKeys.spendingKey,
            ),
          },
        ]
      : [],
    signal: params.signal,
  });
  const recoveryDigest = recovery?.digest;
  const isAdapterAction = params.kind === "swap" || params.kind === "bridge";
  const action =
    params.kind === "withdraw"
      ? resolveFlatJoinSplitV2Action({
          kind: "withdraw",
          snapshot,
          recoveryDigest,
          recipient: params.recipient!,
          fee: params.fee ?? 0n,
          outputCount: params.outputs.length as 0 | 1,
        })
      : params.kind === "withdraw_batch"
        ? resolveFlatJoinSplitV2Action({
            kind: "withdraw_batch",
            snapshot,
            recoveryDigest,
            recipients: params.recipients!,
            amounts: params.amounts!,
            fee: params.fee ?? 0n,
            outputCount: params.outputs.length as 0 | 1,
          })
        : isAdapterAction
          ? resolveFlatJoinSplitV2Action({
              kind: params.kind as "swap" | "bridge",
              snapshot,
              recoveryDigest,
              // The adapter stands in for the recipient, so the proof is bound to
              // the exact adapter the pool will call. Read from the pool rather
              // than supplied, so a stale client cannot bind a rotated adapter.
              recipient: params.recipient!,
              fee: params.fee ?? 0n,
              outputCount: params.outputs.length as 0 | 1,
              auxDataHash: params.auxDataHash!,
            })
          : resolveFlatJoinSplitV2Action({
              kind: params.kind,
              snapshot,
              recoveryDigest,
            } as any);

  await withReadCancellation(
    () =>
      validateInputs({
        provider: params.provider,
        pool: params.pool,
        tokenAddress: params.tokenAddress,
        spendingKeys: params.spendingKeys,
        selected: params.selected,
        expectedTokenHash: snapshot.sourceTokenHash,
      }),
    params.signal,
  );
  const provingStarted = Date.now();
  const transaction = await withFlatJoinSplitV2Prover(
    async (prover) => {
      const inputs = params.selected.map(({ note }) =>
        inputWitness(note, tree.path(note.commitment), params.spendingKeys),
      );
      const outputWitnesses = params.outputs.map(outputWitness);
      const request: FlatJoinSplitV2ProveRequest = {
        arity_id: assertFlatArityAllowed(
          selectFlatJoinSplitV2Arity(inputs.length, outputWitnesses.length),
        ),
        merkle_root: tree.root,
        inputs,
        outputs: outputWitnesses,
        withdraw_amount: decimalScalar(params.withdrawAmount ?? 0n),
        action_token_hash: action.actionTokenHash,
        output_token_hash: action.outputTokenHash,
        action_context: action.actionContext,
        spend_secret_scalar: decimalScalar(
          params.spendingKeys.spendSecretScalar,
        ),
      };
      const bundle = await proveFlatJoinSplitV2(
        prover as any,
        request,
        params.signal,
      );
      return params.kind === "joinsplit"
        ? buildFlatJoinSplitV2Transaction({
            pool: params.pool,
            chainId: params.chainId,
            token: params.tokenAddress,
            bundle,
            recoveryDigest,
          })
        : params.kind === "withdraw"
          ? buildFlatWithdrawV2Transaction({
              pool: params.pool,
              chainId: params.chainId,
              token: params.tokenAddress,
              recipient: params.recipient!,
              amount: params.withdrawAmount!,
              fee: params.fee ?? 0n,
              feeRecipient: snapshot.feeRecipient,
              bundle,
              recoveryDigest,
            })
          : params.kind === "withdraw_batch"
            ? buildFlatWithdrawBatchV2Transaction({
                pool: params.pool,
                chainId: params.chainId,
                token: params.tokenAddress,
                recipients: params.recipients!,
                amounts: params.amounts!,
                fee: params.fee ?? 0n,
                feeRecipient: snapshot.feeRecipient,
                bundle,
                recoveryDigest,
              })
            : params.kind === "swap"
              ? buildFlatPrivateSwapV2Transaction({
                  pool: params.pool,
                  chainId: params.chainId,
                  token: params.tokenAddress,
                  adapter: params.recipient!,
                  amount: params.withdrawAmount!,
                  fee: params.fee ?? 0n,
                  feeRecipient: snapshot.feeRecipient,
                  swapData: params.swapData!,
                  bundle,
                  recoveryDigest,
                })
              : params.kind === "bridge"
                ? buildFlatPrivateBridgeV2Transaction({
                    pool: params.pool,
                    chainId: params.chainId,
                    token: params.tokenAddress,
                    adapter: params.recipient!,
                    amount: params.withdrawAmount!,
                    fee: params.fee ?? 0n,
                    feeRecipient: snapshot.feeRecipient,
                    bridgeData: params.bridgeData!,
                    bundle,
                    recoveryDigest,
                  })
                : buildFlatMigrateV2Transaction({
                    pool: params.pool,
                    chainId: params.chainId,
                    token: params.tokenAddress,
                    bundle,
                    recoveryDigest,
                  });
    },
    { signal: params.signal, onProgress: progressLog(params.onLog) as any },
  );
  // `withFlatJoinSplitV2Prover` has terminated the worker here. Do not keep
  // the proving key/WASM heap alive while simulating, prompting, or mining.
  assertNotAborted(params.signal, "after proving");
  params.onLog(
    `Browser proof ready (${((Date.now() - provingStarted) / 1000).toFixed(1)}s)`,
    "info",
  );
  return recovery
    ? { ...transaction, data: recovery.wrap(transaction.data) }
    : transaction;
}

export async function proveAndSend(
  params: FlatJoinSplitV2SpendParams,
): Promise<{
  txHash: string;
  outputs: JoinSplitNote[];
  journalId: string;
}> {
  const route = await chooseFlatRelayRoute(params);
  if (
    route.relay &&
    ["withdraw", "withdraw_batch", "swap", "bridge"].includes(params.kind)
  ) {
    if ((params.fee ?? 0n) <= 0n)
      fail("relayed operation requires a positive fee before proving");
    const feeRecipient = await new ethers.Contract(
      params.pool,
      ["function feeRecipient() view returns (address)"],
      params.provider,
    ).feeRecipient();
    assertFlatRelayFeeRecipient(route, feeRecipient, params.fee!);
  }
  const transaction = await prepareFlatJoinSplitV2Spend({
    ...params,
    expectedRelayerAddress: route.relayerAddress,
  });
  await params.beforeDisclosure?.();
  assertNotAborted(params.signal, "before settlement disclosure");
  const expectedSignerAddress = await params.signer.getAddress();
  const sourceNullifiers = await Promise.all(
    params.selected.map(({ note }) =>
      computeNullifier(
        params.spendingKeys.nullifyingKey,
        note.tokenHash,
        note.leafIndex,
      ),
    ),
  );
  const journalScope: FlatJoinSplitV2JournalScope = {
    chainId: params.chainId,
    poolAddress: params.pool,
    accountAddress: expectedSignerAddress,
    spendingKey: params.spendingKeys.spendingKey,
  };
  return sendSimulated(
    params.signer,
    params.provider,
    transaction,
    params.pool,
    params.outputs,
    params.onLog,
    expectedSignerAddress,
    async (signedIntent) => {
      const journal = await prepareFlatJoinSplitV2SettlementJournal(
        journalScope,
        {
          kind: params.kind as FlatJoinSplitV2JournalKind,
          proofDisclosure: "undisclosed",
          transaction: {
            to: signedIntent.to,
            from: expectedSignerAddress,
            data: signedIntent.data,
            value: signedIntent.value.toString(),
            chainId: Number(signedIntent.chainId),
          },
          sourceCommitments: params.selected.map(({ note }) => note.commitment),
          sourceNullifiers,
          outputNotes: params.outputs,
          deliveries: params.deliveries,
          swapOutputSeed: params.swapOutputSeed,
        },
      );
      await persistFlatJournalBackup(journalScope, journal.id, {
        signal: params.signal,
      });
      return { id: journal.id, scope: journalScope };
    },
    route.relay,
    params.signal,
    params.swapOutputSeed,
    getBaseReadProvider(),
  );
}
